import { useState, useRef, useEffect, useMemo } from 'react';
import { geoMercator, geoPath } from 'd3-geo';
import { feature } from 'topojson-client';
import { FeatureCollection, Geometry } from 'geojson';
import { useTopology } from '../../hooks/useTopology';
import { useOutages } from '../../hooks/useOutages';
import { useChartInteraction } from '../../contexts/ChartInteractionContext';
import { LoadingSkeleton } from '../../components/atoms/LoadingSkeleton/LoadingSkeleton';
import { ErrorMessage } from '../../components/atoms/ErrorMessage/ErrorMessage';
import { outageSeverityClass, OUTAGE_SEVERITY_BUCKETS } from './outageSeverity';
import './CommunityMap.css';

interface CommunityProperties {
  COMM_NM: string;
  COUNTY: string;
  UNIQUE_ID: string;
  LAST_EDI_1?: string;
}

type CommunityFeatureCollection = FeatureCollection<Geometry, CommunityProperties>;

const COUNTY_NAMES: Record<string, string> = {
  KNS: 'Kings',
  PRN: 'Prince',
  QUN: 'Queens',
};



export function CommunityMap() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
  const [tooltipData, setTooltipData] = useState<{
    name: string;
    county: string;
    customersOut: number;
    x: number;
    y: number;
  } | null>(null);

  const { data: topology, isLoading: topologyLoading, error: topologyError } = useTopology();
  const { data: outageData } = useOutages();
  const { hoveredCommunity, setHoveredCommunity } = useChartInteraction();

  // Observe container size
  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        const { width } = entry.contentRect;
        // Just need non-zero dimensions for rendering, viewBox handles actual scaling
        setDimensions({ width, height: width / 2 });
      }
    });

    resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  // Convert TopoJSON to GeoJSON
  const geojson = useMemo((): CommunityFeatureCollection | null => {
    if (!topology) return null;
    return feature(topology, topology.objects.community_polygon) as unknown as CommunityFeatureCollection;
  }, [topology]);

  // Build outage lookup map (uppercase name -> outage data)
  const outageMap = useMemo(() => {
    if (!outageData?.communities) return new Map();
    return new Map(
      outageData.communities.map((c) => [c.name.toUpperCase(), c])
    );
  }, [outageData]);

  // Create projection and path generator
  const { projection, pathGenerator } = useMemo(() => {
    if (!geojson || dimensions.width === 0) {
      return { projection: null, pathGenerator: null };
    }

    // Use fixed internal size, viewBox handles scaling
    const internalWidth = 800;
    const internalHeight = 350;
    const proj = geoMercator().fitSize([internalWidth, internalHeight], geojson);

    return {
      projection: proj,
      pathGenerator: geoPath().projection(proj),
    };
  }, [geojson, dimensions]);

  const handleMouseMove = (
    e: React.MouseEvent,
    name: string,
    county: string,
    customersOut: number
  ) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;

    setTooltipData({
      name,
      county: COUNTY_NAMES[county] || county,
      customersOut,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleMouseLeave = () => {
    setTooltipData(null);
    setHoveredCommunity(null);
  };

  const renderContent = () => {
    if (topologyLoading) {
      return <LoadingSkeleton variant="text" />;
    }

    if (topologyError) {
      return <ErrorMessage message="Failed to load map data" />;
    }

    if (!geojson || !pathGenerator || dimensions.width === 0) {
      return null;
    }

    return (
      <>
        <svg
          viewBox="0 0 800 350"
          preserveAspectRatio="xMidYMid meet"
          className="CommunityMap-svg"
        >
          <g>
            {geojson.features.map((feat, i) => {
              const name = feat.properties?.COMM_NM || '';
              const county = feat.properties?.COUNTY || '';
              const outage = outageMap.get(name);
              const customersOut = outage?.customersOut || 0;
              const severity = outageSeverityClass(customersOut);
              const isHovered = hoveredCommunity?.toUpperCase() === name;

              return (
                <path
                  key={`${feat.properties?.COUNTY}-${feat.properties?.UNIQUE_ID}-${i}`}
                  d={pathGenerator(feat.geometry) || ''}
                  className={`CommunityMap-community ${severity} ${isHovered ? 'hovered' : ''}`}
                  onMouseEnter={() => setHoveredCommunity(name)}
                  onMouseMove={(e) => handleMouseMove(e, name, county, customersOut)}
                  onMouseLeave={handleMouseLeave}
                />
              );
            })}
          </g>
        </svg>

        <div className="CommunityMap-legend">
          {OUTAGE_SEVERITY_BUCKETS.map((b) => (
            <span key={b.className} className="CommunityMap-legendItem">
              <span className={`CommunityMap-legendSwatch ${b.className}`} />
              {b.label}
            </span>
          ))}
        </div>

        {tooltipData && (
          <div
            className="CommunityMap-tooltip"
            style={{
              left: tooltipData.x + 10,
              top: tooltipData.y - 10,
            }}
          >
            <div className="CommunityMap-tooltipName">{tooltipData.name}</div>
            <div className="CommunityMap-tooltipCounty">{tooltipData.county} County</div>
            <div className={`CommunityMap-tooltipStatus ${tooltipData.customersOut > 0 ? 'offline' : 'online'}`}>
              {tooltipData.customersOut > 0
                ? `${tooltipData.customersOut} ${tooltipData.customersOut === 1 ? 'customer' : 'customers'} offline`
                : 'Online'}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="CommunityMap-container" ref={containerRef}>
      {renderContent()}
    </div>
  );
}
